import { cloneDeep, isEmpty } from 'lodash-es';
import React from 'react';

import { TargetingRequestedPodDurationMarks } from '~/src/dash/api/console-api';
import { generateCountryGroupOptionsList } from '~/src/dash/api/country-group';
import { generateAudienceSegmentOptionsList } from '~/src/dash/apiV2/audienceSegments';
import { generateContextualSegmentOptionsList } from '~/src/dash/apiV2/contextualSegments';
import {
  DeviceOptions,
  DeviceORTBTypeOptions,
  generateCountryOptions,
  generateDeviceSubTypeOptionsList,
  PodSequenceTargetingOptions,
  PositionTargetingOptions,
  SamsungDeviceSubTypeOptions,
  SlotOptions,
  TargetBoolOptions,
  TargetedAdBreakOptions,
  TargetingCCPAOptions,
  TargetingCOPPAOptions,
  TargetingLiveStreamOptions,
  TargetingPresetKey,
  TargetingPresetKeys,
} from '~/src/dash/apiV2/targeting/targetingPresets';
import {
  generateOptionsListAsPromise,
  ISelectOption,
} from '~/src/dash/components/dropdowns/common';
import {
  convertBooleanIntoEBool,
  convertEBoolIntoBoolean,
  convertSetIntoSingleNumber,
  convertSetIntoString,
  convertSingleNumberIntoArray,
  convertStringIntoArray,
  removeEmojiPrefixFromLabel,
} from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/common/common';
import { DaypartingBuilder } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/DaypartingBuilder';
import {
  defaultDaypartingParsed,
  defaultDaypartingUI,
  emptyDaypartingDays,
  generateDaypartingPeriods,
  IDaypartingDays,
  IDaypartingParsed,
  IDaypartingUI,
} from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/DaypartingBuilder/common';
import { LocalTargetingBuilder } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/LocalTargetingBuilder';
import {
  defaultLocalTargetingUI,
  ILocalTargetingParsed,
  ILocalTargetingUI,
} from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/LocalTargetingBuilder/common';
import { RegionsEditor } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/RegionsEditor';
import {
  ISelectWithIncludeAndLogicProps,
  SelectWithIncludeAndLogic,
} from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/SelectWithIncludeAndLogic';
import {
  IStepSliderProps,
  StepSlider,
} from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/StepSlider';
import {
  ISwitchWithTooltipProps,
  SwitchWithTooltip,
} from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/SwitchWithToolTip';
import {
  ITextInputWithIncludeProps,
  TextInputWithInclude,
} from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/TextInputWithInclude';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IUserState } from '~/src/dash/reducers/user-reducers';
import { generateDMAOptions } from '~/src/dash/utils/countries';
import { isPublisherSamsung } from '~/src/dash/utils/publishers';

export type TargetingPresetCategory =
  | 'Inventory'
  | 'Content'
  | 'Geo'
  | 'Dayparting'
  | 'Privacy'
  | 'Custom'
  | 'Device'
  | 'App'
  | 'Deals'
  | 'Streams'
  | 'Segments'
  | 'Site Pages'
  | 'Site Domains';

export const TargetingPresetCategoryKeys: Record<string, TargetingPresetCategory> = {
  INVENTORY: 'Inventory',
  CONTENT: 'Content',
  GEO: 'Geo',
  DAYPARTING: 'Dayparting',
  PRIVACY: 'Privacy',
  CUSTOM: 'Custom',
  DEVICE: 'Device',
  APP: 'App',
  DEALS: 'Deals',
  STREAMS: 'Streams',
  SEGMENTS: 'Segments',
  SITE_PAGES: 'Site Pages',
  SITE_DOMAINS: 'Site Domains',
};

export type TargetingPresetParamKey =
  // INVENTORY
  // | 'Channels'
  // | 'ChannelGroups'
  // | 'ChannelLists'
  | 'Slots'
  | 'EnforceSlotTargeting'
  | 'Position'
  | 'PodSequence'
  | 'RequestedPodDuration'
  | 'CombineVMAPRequests' // DB Name: AdbreakTargeting
  | 'AdBreak' // DB Name: TargetedAdBreak
  | 'GivnTargeting'
  // CONTENT
  | 'ContentChannel'
  | 'ContentDistributor'
  | 'ContentGenre'
  | 'ContentID'
  | 'ContentNetwork'
  | 'ContentProducer'
  | 'ContentRating'
  | 'ContentSeries'
  | 'ContentTitle'
  | 'ContentCategory'
  // GEO
  | 'TargetedRegions' // COUNTRY GROUPS
  | 'TargetedCountries'
  | 'LocalTargeting'
  // DAYPARTING
  | 'Dayparting'
  // PRIVACY
  | 'COPPA'
  | 'LimitedAdTracking'
  | 'CCPA'
  // CUSTOM
  | 'Custom4'
  | 'Custom5'
  | 'Custom6'
  | 'Custom7'
  | 'Custom8'
  | 'Custom9'
  | 'Custom10'
  | 'Custom11'
  | 'Custom12'
  | 'Custom13'
  | 'Custom14'
  | 'Custom15'
  | 'Custom16'
  | 'Custom17'
  | 'Custom18'
  | 'CustomTargeting'
  // DEVICE
  | 'TargetedDevices'
  | 'TargetedDeviceTypes'
  | 'TargetedDeviceSubtypes'
  | 'IPFilters'
  // APP
  | 'AppBundleIDs'
  // DEALS
  | 'DealIDs'
  | 'FreewheelSectionIDs'
  // STREAMS
  | 'TargetedLivestreams'
  // SEGMENTS
  | 'AudienceSegments'
  | 'ContextualSegments'
  // SITE PAGES
  | 'SitePages'
  // SITE DOMAINS
  | 'SiteDomains';

export const TargetingPresetParamKeys: Record<string, TargetingPresetParamKey> = {
  // INVENTORY
  // CHANNELS: 'Channels',
  // CHANNEL_GROUPS: 'ChannelGroups',
  // CHANNEL_LISTS: 'ChannelLists',
  SLOTS: 'Slots',
  ENFORCE_SLOT_TARGETING: 'EnforceSlotTargeting',
  POSITION: 'Position',
  POD_SEQUENCE: 'PodSequence',
  REQUESTED_POD_DURATION: 'RequestedPodDuration',
  COMBINE_VMAP_REQUESTS: 'CombineVMAPRequests', // DB: AdbreakTargeting
  AD_BREAK: 'AdBreak', // DB: TargetedAdBreak
  GIVN_TARGETING: 'GivnTargeting',
  // CONTENT
  CONTENT_CHANNEL: 'ContentChannel',
  CONTENT_DISTRIBUTOR: 'ContentDistributor',
  CONTENT_GENRE: 'ContentGenre',
  CONTENT_ID: 'ContentID',
  CONTENT_NETWORK: 'ContentNetwork',
  CONTENT_PRODUCER: 'ContentProducer',
  CONTENT_RATING: 'ContentRating',
  CONTENT_SERIES: 'ContentSeries',
  CONTENT_TITLE: 'ContentTitle',
  CONTENT_CATEGORY: 'ContentCategory',
  // GEO
  TARGETED_REGIONS: 'TargetedRegions', // COUNTRY GROUPS
  TARGETED_COUNTRIES: 'TargetedCountries',
  LOCAL_TARGETING: 'LocalTargeting',
  // DAYPARTING
  DAYPARTING: 'Dayparting',
  // PRIVACY
  COPPA: 'COPPA',
  LIMITED_AD_TRACKING: 'LimitedAdTracking',
  CCPA: 'CCPA',
  // CUSTOM
  CUSTOM_4: 'Custom4',
  CUSTOM_5: 'Custom5',
  CUSTOM_6: 'Custom6',
  CUSTOM_7: 'Custom7',
  CUSTOM_8: 'Custom8',
  CUSTOM_9: 'Custom9',
  CUSTOM_10: 'Custom10',
  CUSTOM_11: 'Custom11',
  CUSTOM_12: 'Custom12',
  CUSTOM_13: 'Custom13',
  CUSTOM_14: 'Custom14',
  CUSTOM_15: 'Custom15',
  CUSTOM_16: 'Custom16',
  CUSTOM_17: 'Custom17',
  CUSTOM_18: 'Custom18',
  CUSTOM_TARGETING: 'CustomTargeting',
  // DEVICE
  TARGETED_DEVICES: 'TargetedDevices',
  TARGETED_DEVICE_TYPES: 'TargetedDeviceTypes',
  TARGETED_DEVICE_SUBTYPES: 'TargetedDeviceSubtypes',
  IP_FILTERS: 'IPFilters',
  // APP
  APP_BUNDLE_IDS: 'AppBundleIDs',
  // DEALS
  DEAL_IDS: 'DealIDs',
  FREEWHEEL_SECTION_IDS: 'FreewheelSectionIDs',
  // STREAMS
  TARGETED_LIVESTREAMS: 'TargetedLivestreams',
  // SEGMENTS
  AUDIENCE_SEGMENTS: 'AudienceSegments',
  CONTEXTUAL_SEGMENTS: 'ContextualSegments',
  // SITE PAGES
  SITE_PAGES: 'SitePages',
  // SITE DOMAINS
  SITE_DOMAINS: 'SiteDomains',
};

const generateGenericPlaceholder = (label: string) => {
  return `Enter ${label} here, comma separated.`;
};

const generateContentPlaceholder = (label: string) => {
  return `Enter ${label} to be bid on here, comma separated.`;
};

const generateCustomPlaceholder = (label: string) => {
  return `Enter ${label} here. Can be comma separated.`;
};

const customTooltipMessage = `Checks for custom parameters set in your request to Publica's endpoint and includes or excludes the bidder. Can be comma separated.`;

const AppBundleTooltipMessage = (
  <div>
    App Bundle ID to bid on
    <br />- Android: com.hulu.plus
    <br />- Apple TV: 482066631
    <br />- Roku: 12
    <br />- Fire TV: B004U7BXBO
    <br />- Samsung: G15115002089
    <br /> More Info{' '}
    <a
      href='https://iabtechlab.com/wp-content/uploads/2020/08/IAB-Tech-Lab-OTT-store-assigned-App-Identification-Guidelines-2020.pdf'
      rel='noopener'
      target='_blank'
    >
      Here
    </a>
  </div>
);

const convertSlotsSelectedValue = (value: Set<number>) => {
  if (value.size === SlotOptions.length) {
    return '';
  } else {
    return Array.from(value).join(',');
  }
};

const convertSlotsResponseValue = (value: string) => {
  if (value && value !== '0') {
    return value.split(',').map(Number);
  } else {
    return SlotOptions.map((option) => option.id);
  }
};

const convertDevicesSelectedValue = (value: Set<number>) => {
  if (value.size === DeviceOptions.length) {
    return '0';
  } else {
    return Array.from(value).join(',');
  }
};

const convertDevicesResponseValue = (value: string) => {
  switch (true) {
    case !value:
    case value === '0':
      return DeviceOptions.map((option) => option.id);
    default:
      return value.split(',').map(Number);
  }
};

const convertDeviceTypesSelectedValue = (value: Set<number>) => {
  if (value.size === DeviceORTBTypeOptions.length) {
    return '';
  } else {
    return Array.from(value).join(',');
  }
};

const convertDeviceTypesResponseValue = (value: string) => {
  switch (true) {
    case !value:
      return DeviceORTBTypeOptions.map((option) => option.id);
    default:
      return value.split(',').map(Number);
  }
};

const convertDeviceSubTypesSelectedValue = (value: Set<number>) => {
  if (value.size === SamsungDeviceSubTypeOptions.length) {
    return '0';
  } else {
    return Array.from(value).join(',');
  }
};

const convertDeviceSubTypesResponseValue = (value: string) => {
  switch (true) {
    case !value:
    case value === '0':
      return SamsungDeviceSubTypeOptions.map((option) => option.id);
    default:
      return value.split(',').map(Number);
  }
};

const convertLivestreamResponseValue = (value: string) => {
  if (!value || value === '0') {
    return TargetingLiveStreamOptions.map((option) => option.id);
  } else {
    return value.split(',').map(Number);
  }
};

const convertLivestreamSelectedValue = (value: Set<number>) => {
  if (value.size === TargetingLiveStreamOptions.length) {
    return '0';
  } else {
    return Array.from(value).join(',');
  }
};

const parseLocalTargetingForUI = (localTargetingBlob: string) => {
  const localTargeting: ILocalTargetingParsed[] = !!localTargetingBlob
    ? JSON.parse(localTargetingBlob)
    : [];

  const localTargetingUI: ILocalTargetingUI[] = [];
  let reactKey = 1001;
  localTargeting.forEach((lt) => {
    localTargetingUI.push({
      type: lt.type || 'country',
      country: lt.country || '',
      states: lt.states || '',
      areStatesCommaSeparated: !!lt.areStatesCommaSeparated,
      cities: lt.cities || '',
      areCitiesCommaSeparated: !!lt.areCitiesCommaSeparated,
      zips: lt.zips || '',
      dmas: lt.dmas || '',
      areDMAsCommaSeparated: !!lt.areDMAsCommaSeparated,
      reactKey,
    });
    reactKey++;
  });

  if (isEmpty(localTargetingUI)) {
    localTargetingUI.push(cloneDeep(defaultLocalTargetingUI));
    localTargetingUI[0].reactKey = reactKey;
  }

  return localTargetingUI;
};

const convertStringToTrimmedArray = (value: string) => {
  if (!value) return [];
  return value.split(',').map((s) => s.trim());
};

const checkForDuplicates = (array: string[]) => {
  return new Set(array).size !== array.length;
};

const convertLocalTargetingUI = (locations: ILocalTargetingUI[]) => {
  const result: ILocalTargetingParsed[] = [];
  const errors: Set<string> = new Set([]);
  locations.forEach((loc) => {
    if (loc.country) {
      // validate
      // states
      const states = convertStringToTrimmedArray(loc.states);
      if (checkForDuplicates(states)) {
        errors.add('Local Targeting: Please remove duplicate state values.');
      }
      // cities
      const cities = convertStringToTrimmedArray(loc.cities);
      if (checkForDuplicates(cities)) {
        errors.add('Local Targeting: Please remove duplicate city values.');
      }
      // zips
      const zips = convertStringToTrimmedArray(loc.zips);
      if (zips.some((zip) => zip.length < 3 && zip !== Number(zip).toString())) {
        errors.add('Local Targeting: Invalid zip codes found in Local Targeting.');
      }
      if (checkForDuplicates(zips)) {
        errors.add('Local Targeting: Please remove duplicate zip codes.');
      }
      // dmas
      const dmas = convertStringToTrimmedArray(loc.dmas);
      if (checkForDuplicates(dmas)) {
        errors.add('Local Targeting: Please remove duplicate DMAs.');
      }

      result.push({
        type: loc.type,
        country: loc.country,
        states: states.join(','),
        areStatesCommaSeparated: !!loc.areStatesCommaSeparated,
        cities: cities.join(','),
        areCitiesCommaSeparated: !!loc.areCitiesCommaSeparated,
        zips: zips.join(','),
        dmas: dmas.join(','),
        areDMAsCommaSeparated: !!loc.areDMAsCommaSeparated,
      });
    }
  });
  return {
    value: JSON.stringify(result),
    errors: Array.from(errors),
  };
};

const parseDaypartingForUI = (daypartingBlob: string, session: ISessionState): IDaypartingUI => {
  const dayparting: IDaypartingParsed = !!daypartingBlob ? JSON.parse(daypartingBlob) : {};

  const daypartingUI = cloneDeep(defaultDaypartingUI);

  daypartingUI.Timezone = dayparting?.Timezone || session.publisher.Timezone;
  daypartingUI.Enabled = dayparting?.Enabled || false;
  daypartingUI.EnableViewerTimezone = dayparting?.EnableViewerTimezone || false;

  const schedule = dayparting?.Schedule || defaultDaypartingParsed.Schedule;
  daypartingUI.Periods = generateDaypartingPeriods(schedule);

  return daypartingUI;
};

const convertDaypartingUI = (props: IDaypartingUI) => {
  const errors: string[] = [];
  const { Periods, EnableViewerTimezone, Enabled, Timezone } = props;
  const daypartingDays = cloneDeep(emptyDaypartingDays);
  const dayKeys = Object.keys(daypartingDays);

  if (Periods.length > 0) {
    Object.values(Periods).forEach((period) => {
      period.days.forEach((day: keyof IDaypartingDays) => {
        daypartingDays[day].push([period.start, period.end]);
      });
    });
  }

  // sort intervals
  dayKeys.forEach((dayKey: keyof IDaypartingDays) => {
    daypartingDays[dayKey] = daypartingDays[dayKey].sort((a, b) => {
      if (a[0].isBefore(b[0])) {
        return -1;
      } else if (a[0].isAfter(b[0])) {
        return 1;
      }
      return 0;
    });
  });

  // merge intervals
  dayKeys.forEach((dayKey: keyof IDaypartingDays) => {
    const results = [] as [moment.Moment, moment.Moment][];
    daypartingDays[dayKey].forEach((interval) => {
      const prev = results[results.length - 1];
      if (!results.length || prev[1].isBefore(interval[0])) {
        results.push(interval);
      } else {
        if (prev[1].isBefore(interval[1])) {
          prev[1] = interval[1];
        }
      }
      daypartingDays[dayKey] = results;
    });
  });

  const emptyDayparting = cloneDeep(defaultDaypartingParsed);

  // convert moments to string
  dayKeys.forEach((dayKey: keyof IDaypartingDays) => {
    daypartingDays[dayKey].forEach((interval) => {
      emptyDayparting.Schedule[dayKey].push([
        interval[0].format('HH:mm'),
        interval[1].format('HH:mm'),
      ]);
    });
  });

  // populate rest for conversion
  emptyDayparting.EnableViewerTimezone = EnableViewerTimezone;
  emptyDayparting.Enabled = Enabled;
  emptyDayparting.Timezone = Timezone;

  return {
    value: JSON.stringify(emptyDayparting),
    errors,
  };
};

const validateTextInput = (value: string, parameterLabel: string) => {
  if (!value) {
    return [`${parameterLabel}: Input is empty.`];
  }
  return [];
};

interface IConvertedPayload {
  value: any;
  errors: string[];
}

interface ICustomPresetConversion {
  convertResponse: (value: any, session: ISessionState) => any;
  convertPayload: (value: any) => IConvertedPayload;
  uiKey: TargetingPresetKey;
  apiKey: TargetingPresetKey;
}

interface ICustomValidation {
  validateKey: TargetingPresetKey;
  validateFn: (value: any, parameterLabel: string) => string[];
}

export interface ITargetingPresetParam {
  key: TargetingPresetParamKey;
  label: string; // displayed title
  categoryKey: TargetingPresetCategory;
  isSuperUserOnly?: boolean;
  checkIfHidden?: (publisherID: string, isSuperUser: boolean) => boolean; // check if publisher specific
  Component?: (props: any) => JSX.Element;
  props?: Record<string, any>;
  apiKeys: TargetingPresetKey[]; // DO NOT INCLUDE KEYS IN CUSTOMUICONVERSIONS!
  customUIConversions?: ICustomPresetConversion;
  customValidation?: ICustomValidation;
}

export const TargetingPresetParameters: Record<TargetingPresetParamKey, ITargetingPresetParam> = {
  // INVENTORY
  // TODO: SAVE FOR NOW BUT REMOVE BEFORE GENERAL RELEASE
  // Channels: {
  //   key: TargetingPresetParamKeys.CHANNELS,
  //   label: 'Channels',
  //   categoryKey: TargetingPresetCategoryKeys.INVENTORY,
  //   apiKeys: [TargetingPresetKeys.SITE_IDS],
  //   Component: SelectWithIncludeAndLogic,
  //   props: {
  //     placeholder: 'Select Channels',
  //     singularLabel: 'Channel',
  //     listLabel: 'Channels',
  //     listKey: 'Channels',
  //     valueKey: TargetingPresetKeys.SITE_IDS,
  //     enableActiveFilter: true,
  //     enableLabelTooltip: true,
  //     displayID: true,
  //     key: TargetingPresetKeys.SITE_IDS,
  //     onLoadDisplayActiveOnly: true,
  //   } as ISelectWithIncludeAndLogicProps,
  // },
  // ChannelGroups: {
  //   key: TargetingPresetParamKeys.CHANNEL_GROUPS,
  //   label: 'Channel Groups',
  //   categoryKey: TargetingPresetCategoryKeys.INVENTORY,
  //   apiKeys: [TargetingPresetKeys.CHANNEL_GROUP_IDS],
  //   Component: SelectWithIncludeAndLogic,
  //   props: {
  //     placeholder: 'Select Channel Groups',
  //     singularLabel: 'Channel Group',
  //     listLabel: 'Channel Groups',
  //     listKey: 'ChannelGroups',
  //     valueKey: TargetingPresetKeys.CHANNEL_GROUP_IDS,
  //     enableLabelTooltip: true,
  //     key: TargetingPresetKeys.CHANNEL_GROUP_IDS,
  //   } as ISelectWithIncludeAndLogicProps,
  // },
  // ChannelLists: {
  //   key: TargetingPresetParamKeys.CHANNEL_LISTS,
  //   label: 'Channel Lists',
  //   categoryKey: TargetingPresetCategoryKeys.INVENTORY,
  //   apiKeys: [TargetingPresetKeys.CHANNEL_LIST_IDS],
  //   Component: ChannelListsForm,
  //   props: {
  //     placeholder: 'Select Channel Lists',
  //     singularLabel: 'Channel List',
  //     listLabel: 'Channel Lists',
  //     listKey: 'ChannelLists',
  //     valueKey: TargetingPresetKeys.CHANNEL_LIST_IDS,
  //     enableLabelTooltip: true,
  //     key: TargetingPresetKeys.CHANNEL_LIST_IDS,
  //   } as ISelectWithIncludeAndLogicProps,
  // },
  Slots: {
    key: TargetingPresetParamKeys.SLOTS,
    label: 'Slots',
    categoryKey: TargetingPresetCategoryKeys.INVENTORY,
    apiKeys: [TargetingPresetKeys.SLOTS],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'All Slots Selected',
      singularLabel: 'Slot',
      listLabel: 'Slots',
      listKey: 'Slots',
      valueKey: TargetingPresetKeys.SLOTS,
      convertResponseValue: convertSlotsResponseValue,
      convertSelectedValue: convertSlotsSelectedValue,
      key: TargetingPresetKeys.SLOTS,
      disableSort: true,
      tooltip: 'If you want to bid on specific slots, contact your account manager.',
    } as ISelectWithIncludeAndLogicProps,
  },
  EnforceSlotTargeting: {
    key: TargetingPresetParamKeys.ENFORCE_SLOT_TARGETING,
    label: 'Enforce Slot Targeting',
    categoryKey: TargetingPresetCategoryKeys.INVENTORY,
    apiKeys: [TargetingPresetKeys.ENFORCE_SLOT_TARGETING],
    Component: SwitchWithTooltip,
    props: {
      key: TargetingPresetKeys.ENFORCE_SLOT_TARGETING,
      valueKey: TargetingPresetKeys.ENFORCE_SLOT_TARGETING,
      activeLabel: 'Enabled',
      inactiveLabel: 'Disabled',
      convertResponseValue: convertEBoolIntoBoolean,
      convertSwitchValue: convertBooleanIntoEBool,
    } as ISwitchWithTooltipProps,
  },
  Position: {
    key: TargetingPresetParamKeys.POSITION,
    label: 'Position',
    categoryKey: TargetingPresetCategoryKeys.INVENTORY,
    apiKeys: [TargetingPresetKeys.POSITION_TARGETING],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'Select Position',
      singularLabel: 'Position',
      listLabel: 'Positions',
      listKey: 'TargetingPositions',
      valueKey: TargetingPresetKeys.POSITION_TARGETING,
      selectedLimit: 1,
      isSelectionRequired: true,
      convertResponseValue: convertSingleNumberIntoArray,
      convertSelectedValue: convertSetIntoSingleNumber,
      key: TargetingPresetKeys.POSITION_TARGETING,
      disableSort: true,
    } as ISelectWithIncludeAndLogicProps,
  },
  PodSequence: {
    key: TargetingPresetParamKeys.POD_SEQUENCE,
    label: 'Pod Sequence',
    categoryKey: TargetingPresetCategoryKeys.INVENTORY,
    apiKeys: [TargetingPresetKeys.POD_SEQUENCE_TARGETING],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'Select Pod Sequence',
      singularLabel: 'Pod Sequence',
      listLabel: 'Pod Sequences',
      listKey: 'PodSequences',
      valueKey: TargetingPresetKeys.POD_SEQUENCE_TARGETING,
      selectedLimit: 1,
      isSelectionRequired: true,
      convertResponseValue: convertSingleNumberIntoArray,
      convertSelectedValue: convertSetIntoSingleNumber,
      key: TargetingPresetKeys.POD_SEQUENCE_TARGETING,
      disableSort: true,
    } as ISelectWithIncludeAndLogicProps,
  },
  RequestedPodDuration: {
    key: TargetingPresetParamKeys.REQUESTED_POD_DURATION,
    label: 'Requested Pod Duration',
    categoryKey: TargetingPresetCategoryKeys.INVENTORY,
    apiKeys: [
      TargetingPresetKeys.TARGETING_REQUESTED_POD_DURATION_MIN,
      TargetingPresetKeys.TARGETING_REQUESTED_POD_DURATION_MAX,
    ],
    Component: StepSlider,
    props: {
      key: TargetingPresetKeys.TARGETING_REQUESTED_POD_DURATION_MIN,
      minValueKey: TargetingPresetKeys.TARGETING_REQUESTED_POD_DURATION_MIN,
      maxValueKey: TargetingPresetKeys.TARGETING_REQUESTED_POD_DURATION_MAX,
      defaultMin: 0,
      defaultMax: 300,
      generateMarks: TargetingRequestedPodDurationMarks,
      note: '300 equals no max.',
      measurement: 'sec',
      tooltip:
        'Enter min and max ad duration in seconds to be targeted. Targeting is inclusive. For example, if you want to exclude 60 from 0-60, enter 0-59. Maximum duration defaults to no max unless defined.',
    } as IStepSliderProps,
  },
  CombineVMAPRequests: {
    key: TargetingPresetParamKeys.COMBINE_VMAP_REQUESTS,
    label: 'Combine VMAP Requests *',
    categoryKey: TargetingPresetCategoryKeys.INVENTORY,
    apiKeys: [TargetingPresetKeys.ADBREAK_TARGETING],
    Component: SwitchWithTooltip,
    props: {
      key: TargetingPresetKeys.ADBREAK_TARGETING,
      valueKey: TargetingPresetKeys.ADBREAK_TARGETING,
      activeLabel: 'Enabled',
      inactiveLabel: 'Disabled',
      convertResponseValue: (value: string) => value === '1',
      convertSwitchValue: (value: boolean) => (value ? '1' : ''),
      tooltip: 'If you want to combine VMAP requests, contact your account manager.',
    } as ISwitchWithTooltipProps,
    isSuperUserOnly: true,
  },
  AdBreak: {
    key: TargetingPresetParamKeys.AD_BREAK,
    label: 'Ad Break *',
    categoryKey: TargetingPresetCategoryKeys.INVENTORY,
    apiKeys: [TargetingPresetKeys.TARGETED_AD_BREAK],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'Select Ad Break',
      singularLabel: 'Ad Break',
      listLabel: 'Ad Breaks',
      listKey: 'AdBreaks',
      valueKey: TargetingPresetKeys.TARGETED_AD_BREAK,
      selectedLimit: 1,
      isSelectionRequired: true,
      convertResponseValue: convertSingleNumberIntoArray,
      convertSelectedValue: convertSetIntoSingleNumber,
      key: TargetingPresetKeys.TARGETED_AD_BREAK,
      disableSort: true,
    } as ISelectWithIncludeAndLogicProps,
    isSuperUserOnly: true,
  },
  // samsung only
  GivnTargeting: {
    key: TargetingPresetParamKeys.GIVN_TARGETING,
    label: 'Givn Targeting',
    categoryKey: TargetingPresetCategoryKeys.INVENTORY,
    apiKeys: [TargetingPresetKeys.TARGETING_GIVN_PARAM_EXISTS],
    Component: SwitchWithTooltip,
    props: {
      key: TargetingPresetKeys.TARGETING_GIVN_PARAM_EXISTS,
      valueKey: TargetingPresetKeys.TARGETING_GIVN_PARAM_EXISTS,
      activeLabel: 'Enabled',
      inactiveLabel: 'Disabled',
      convertResponseValue: convertEBoolIntoBoolean,
      convertSwitchValue: convertBooleanIntoEBool,
    } as ISwitchWithTooltipProps,
    checkIfHidden: (publisherID) => !isPublisherSamsung(publisherID),
  },
  // CONTENT
  ContentChannel: {
    key: TargetingPresetParamKeys.CONTENT_CHANNEL,
    label: 'Content Channel',
    categoryKey: TargetingPresetCategoryKeys.CONTENT,
    apiKeys: [
      TargetingPresetKeys.TARGETING_CONTENT_CHANNEL,
      TargetingPresetKeys.TARGETING_CONTENT_CHANNEL_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_CONTENT_CHANNEL,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CONTENT_CHANNEL,
      placeholder: generateContentPlaceholder('Content Channel'),
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_CHANNEL,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_CHANNEL_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
  },
  ContentDistributor: {
    key: TargetingPresetParamKeys.CONTENT_DISTRIBUTOR,
    label: 'Content Distributor',
    categoryKey: TargetingPresetCategoryKeys.CONTENT,
    apiKeys: [
      TargetingPresetKeys.TARGETING_CONTENT_DISTRIBUTOR,
      TargetingPresetKeys.TARGETING_CONTENT_DISTRIBUTOR_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_CONTENT_DISTRIBUTOR,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CONTENT_DISTRIBUTOR,
      placeholder: generateContentPlaceholder('Content Distributor'),
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_DISTRIBUTOR,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_DISTRIBUTOR_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
  },
  ContentGenre: {
    key: TargetingPresetParamKeys.CONTENT_GENRE,
    label: 'Content Genre',
    categoryKey: TargetingPresetCategoryKeys.CONTENT,
    apiKeys: [
      TargetingPresetKeys.TARGETING_CONTENT_GENRE,
      TargetingPresetKeys.TARGETING_CONTENT_GENRE_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_CONTENT_GENRE,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CONTENT_GENRE,
      placeholder: generateContentPlaceholder('Content Genre'),
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_GENRE,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_GENRE_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
  },
  ContentID: {
    key: TargetingPresetParamKeys.CONTENT_ID,
    label: 'Content ID',
    categoryKey: TargetingPresetCategoryKeys.CONTENT,
    apiKeys: [
      TargetingPresetKeys.TARGETING_CONTENT_ID,
      TargetingPresetKeys.TARGETING_CONTENT_ID_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_CONTENT_ID,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CONTENT_ID,
      placeholder: generateContentPlaceholder('Content ID'),
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_ID,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_ID_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
  },
  ContentNetwork: {
    key: TargetingPresetParamKeys.CONTENT_NETWORK,
    label: 'Content Network',
    categoryKey: TargetingPresetCategoryKeys.CONTENT,
    apiKeys: [
      TargetingPresetKeys.TARGETING_CONTENT_NETWORK,
      TargetingPresetKeys.TARGETING_CONTENT_NETWORK_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_CONTENT_NETWORK,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CONTENT_NETWORK,
      placeholder: generateContentPlaceholder('Content Network'),
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_NETWORK,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_NETWORK_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
  },
  ContentProducer: {
    key: TargetingPresetParamKeys.CONTENT_PRODUCER,
    label: 'Content Producer',
    categoryKey: TargetingPresetCategoryKeys.CONTENT,
    apiKeys: [
      TargetingPresetKeys.TARGETING_CONTENT_PRODUCER,
      TargetingPresetKeys.TARGETING_CONTENT_PRODUCER_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_CONTENT_PRODUCER,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CONTENT_PRODUCER,
      placeholder: generateContentPlaceholder('Content Producer'),
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_PRODUCER,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_PRODUCER_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
  },
  ContentRating: {
    key: TargetingPresetParamKeys.CONTENT_RATING,
    label: 'Content Rating',
    categoryKey: TargetingPresetCategoryKeys.CONTENT,
    apiKeys: [
      TargetingPresetKeys.TARGETING_CONTENT_RATING,
      TargetingPresetKeys.TARGETING_CONTENT_RATING_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_CONTENT_RATING,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CONTENT_RATING,
      placeholder: generateContentPlaceholder('Content Rating'),
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_RATING,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_RATING_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
  },
  ContentSeries: {
    key: TargetingPresetParamKeys.CONTENT_SERIES,
    label: 'Content Series',
    categoryKey: TargetingPresetCategoryKeys.CONTENT,
    apiKeys: [
      TargetingPresetKeys.TARGETING_CONTENT_SERIES,
      TargetingPresetKeys.TARGETING_CONTENT_SERIES_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_CONTENT_SERIES,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CONTENT_SERIES,
      placeholder: generateContentPlaceholder('Content Series'),
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_SERIES,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_SERIES_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
  },
  ContentTitle: {
    key: TargetingPresetParamKeys.CONTENT_TITLE,
    label: 'Content Title',
    categoryKey: TargetingPresetCategoryKeys.CONTENT,
    apiKeys: [
      TargetingPresetKeys.TARGETING_CONTENT_TITLE,
      TargetingPresetKeys.TARGETING_CONTENT_TITLE_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_CONTENT_TITLE,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CONTENT_TITLE,
      placeholder: generateContentPlaceholder('Content Title'),
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_TITLE,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_TITLE_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
  },
  ContentCategory: {
    key: TargetingPresetParamKeys.CONTENT_CATEGORY,
    label: 'Content Category',
    categoryKey: TargetingPresetCategoryKeys.CONTENT,
    apiKeys: [
      TargetingPresetKeys.TARGETING_CONTENT_CATEGORY,
      TargetingPresetKeys.TARGETING_CONTENT_CATEGORY_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_CONTENT_CATEGORY,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CONTENT_CATEGORY,
      placeholder: generateContentPlaceholder('Content Category'),
      valueKey: TargetingPresetKeys.TARGETING_CONTENT_CATEGORY,
      allowKey: TargetingPresetKeys.TARGETING_CONTENT_CATEGORY_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
  },
  // GEO
  TargetedRegions: {
    key: TargetingPresetParamKeys.TARGETED_REGIONS,
    label: 'Targeted Regions',
    categoryKey: TargetingPresetCategoryKeys.GEO,
    apiKeys: [
      TargetingPresetKeys.TARGETING_COUNTRY_GROUP_IDS,
      TargetingPresetKeys.TARGETING_COUNTRY_GROUP_IDS_IS_ALLOWED,
    ],
    Component: RegionsEditor,
  },
  TargetedCountries: {
    key: TargetingPresetParamKeys.TARGETED_COUNTRIES,
    label: 'Targeted Countries',
    categoryKey: TargetingPresetCategoryKeys.GEO,
    apiKeys: [
      TargetingPresetKeys.TARGETING_DEVICE_COUNTRY,
      TargetingPresetKeys.TARGETING_DEVICE_COUNTRY_IS_ALLOWED,
    ],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'Select Countries',
      singularLabel: 'Country',
      extractStringToSort: removeEmojiPrefixFromLabel,
      listLabel: 'Countries',
      listKey: 'Countries',
      valueKey: TargetingPresetKeys.TARGETING_DEVICE_COUNTRY,
      allowKey: TargetingPresetKeys.TARGETING_DEVICE_COUNTRY_IS_ALLOWED,
      convertResponseValue: convertStringIntoArray,
      convertSelectedValue: convertSetIntoString,
      key: TargetingPresetKeys.TARGETING_DEVICE_COUNTRY,
    } as ISelectWithIncludeAndLogicProps,
  },
  // THIS IS A SPECIAL CASE AND REQUIRES A CUSTOM COMPONENT
  LocalTargeting: {
    key: TargetingPresetParamKeys.LOCAL_TARGETING,
    label: 'Local Targeting',
    categoryKey: TargetingPresetCategoryKeys.GEO,
    apiKeys: [
      TargetingPresetKeys.LOCAL_TARGETING_IS_ALLOWED,
      TargetingPresetKeys.LOCAL_TARGETING_LOGIC,
    ],
    Component: LocalTargetingBuilder,
    customUIConversions: {
      apiKey: TargetingPresetKeys.LOCAL_TARGETING,
      uiKey: TargetingPresetKeys.LOCAL_TARGETING_UI,
      convertResponse: parseLocalTargetingForUI,
      convertPayload: convertLocalTargetingUI,
    },
  },
  // THIS IS A SPECIAL CASE AND REQUIRES A CUSTOM COMPONENT
  Dayparting: {
    key: TargetingPresetParamKeys.DAYPARTING,
    label: 'Dayparting',
    categoryKey: TargetingPresetCategoryKeys.DAYPARTING,
    apiKeys: [TargetingPresetKeys.DAYPARTING_IS_ALLOWED],
    Component: DaypartingBuilder,
    customUIConversions: {
      apiKey: TargetingPresetKeys.DAYPARTING,
      uiKey: TargetingPresetKeys.DAYPARTING_UI,
      convertResponse: parseDaypartingForUI,
      convertPayload: convertDaypartingUI,
    },
  },
  // PRIVACY
  COPPA: {
    key: TargetingPresetParamKeys.COPPA,
    label: 'COPPA',
    categoryKey: TargetingPresetCategoryKeys.PRIVACY,
    apiKeys: [TargetingPresetKeys.TARGETING_COPPA],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'Select COPPA Value',
      singularLabel: 'COPPA Value',
      listLabel: 'COPPA',
      listKey: 'COPPA',
      valueKey: TargetingPresetKeys.TARGETING_COPPA,
      selectedLimit: 1,
      isSelectionRequired: true,
      convertResponseValue: convertSingleNumberIntoArray,
      convertSelectedValue: convertSetIntoSingleNumber,
      key: TargetingPresetKeys.TARGETING_COPPA,
      disableSort: true,
    } as ISelectWithIncludeAndLogicProps,
  },
  LimitedAdTracking: {
    key: TargetingPresetParamKeys.LIMITED_AD_TRACKING,
    label: 'Limited Ad Tracking',
    categoryKey: TargetingPresetCategoryKeys.PRIVACY,
    apiKeys: [TargetingPresetKeys.TARGETING_LIMITED_AD_TRACKING],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'Select Limited Ad Tracking',
      singularLabel: 'Limited Ad Tracking',
      listLabel: 'Limited Ad Tracking',
      listKey: 'LimitedAdTracking',
      valueKey: TargetingPresetKeys.TARGETING_LIMITED_AD_TRACKING,
      selectedLimit: 1,
      isSelectionRequired: true,
      convertResponseValue: convertSingleNumberIntoArray,
      convertSelectedValue: convertSetIntoSingleNumber,
      key: TargetingPresetKeys.TARGETING_LIMITED_AD_TRACKING,
      disableSort: true,
    } as ISelectWithIncludeAndLogicProps,
  },
  CCPA: {
    key: TargetingPresetParamKeys.CCPA,
    label: 'CCPA',
    categoryKey: TargetingPresetCategoryKeys.PRIVACY,
    apiKeys: [TargetingPresetKeys.TARGETING_CCPA],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'Select CCPA Value',
      singularLabel: 'CCPA Value',
      listLabel: 'CCPA',
      listKey: 'CCPA',
      valueKey: TargetingPresetKeys.TARGETING_CCPA,
      selectedLimit: 1,
      isSelectionRequired: true,
      convertResponseValue: convertSingleNumberIntoArray,
      convertSelectedValue: convertSetIntoSingleNumber,
      key: TargetingPresetKeys.TARGETING_CCPA,
      disableSort: true,
    } as ISelectWithIncludeAndLogicProps,
  },
  // CUSTOM
  Custom4: {
    key: TargetingPresetParamKeys.CUSTOM_4,
    label: 'Custom 4',
    categoryKey: TargetingPresetCategoryKeys.CUSTOM,
    apiKeys: [TargetingPresetKeys.CUSTOM_4, TargetingPresetKeys.CUSTOM_4_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_4,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_4,
      placeholder: generateCustomPlaceholder('Custom 4'),
      valueKey: TargetingPresetKeys.CUSTOM_4,
      allowKey: TargetingPresetKeys.CUSTOM_4_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
  },
  Custom5: {
    key: TargetingPresetParamKeys.CUSTOM_5,
    label: 'Custom 5',
    categoryKey: TargetingPresetCategoryKeys.CUSTOM,
    apiKeys: [TargetingPresetKeys.CUSTOM_5, TargetingPresetKeys.CUSTOM_5_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_5,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_5,
      placeholder: generateCustomPlaceholder('Custom 5'),
      valueKey: TargetingPresetKeys.CUSTOM_5,
      allowKey: TargetingPresetKeys.CUSTOM_5_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
  },
  Custom6: {
    key: TargetingPresetParamKeys.CUSTOM_6,
    label: 'Custom 6',
    categoryKey: TargetingPresetCategoryKeys.CUSTOM,
    apiKeys: [TargetingPresetKeys.CUSTOM_6, TargetingPresetKeys.CUSTOM_6_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_6,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_6,
      placeholder: generateCustomPlaceholder('Custom 6'),
      valueKey: TargetingPresetKeys.CUSTOM_6,
      allowKey: TargetingPresetKeys.CUSTOM_6_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
  },
  Custom7: {
    key: TargetingPresetParamKeys.CUSTOM_7,
    label: 'Custom 7',
    categoryKey: TargetingPresetCategoryKeys.CUSTOM,
    apiKeys: [TargetingPresetKeys.CUSTOM_7, TargetingPresetKeys.CUSTOM_7_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_7,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_7,
      placeholder: generateCustomPlaceholder('Custom 7'),
      valueKey: TargetingPresetKeys.CUSTOM_7,
      allowKey: TargetingPresetKeys.CUSTOM_7_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
  },
  Custom8: {
    key: TargetingPresetParamKeys.CUSTOM_8,
    label: 'Custom 8',
    categoryKey: TargetingPresetCategoryKeys.CUSTOM,
    apiKeys: [TargetingPresetKeys.CUSTOM_8, TargetingPresetKeys.CUSTOM_8_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_8,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_8,
      placeholder: generateCustomPlaceholder('Custom 8'),
      valueKey: TargetingPresetKeys.CUSTOM_8,
      allowKey: TargetingPresetKeys.CUSTOM_8_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
  },
  Custom9: {
    key: TargetingPresetParamKeys.CUSTOM_9,
    label: 'Custom 9',
    categoryKey: TargetingPresetCategoryKeys.CUSTOM,
    apiKeys: [TargetingPresetKeys.CUSTOM_9, TargetingPresetKeys.CUSTOM_9_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_9,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_9,
      placeholder: generateCustomPlaceholder('Custom 9'),
      valueKey: TargetingPresetKeys.CUSTOM_9,
      allowKey: TargetingPresetKeys.CUSTOM_9_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
  },
  Custom10: {
    key: TargetingPresetParamKeys.CUSTOM_10,
    label: 'Custom 10',
    categoryKey: TargetingPresetCategoryKeys.CUSTOM,
    apiKeys: [TargetingPresetKeys.CUSTOM_10, TargetingPresetKeys.CUSTOM_10_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_10,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_10,
      placeholder: generateCustomPlaceholder('Custom 10'),
      valueKey: TargetingPresetKeys.CUSTOM_10,
      allowKey: TargetingPresetKeys.CUSTOM_10_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
  },
  Custom11: {
    key: TargetingPresetParamKeys.CUSTOM_11,
    label: 'Custom 11',
    categoryKey: TargetingPresetCategoryKeys.CUSTOM,
    apiKeys: [TargetingPresetKeys.CUSTOM_11, TargetingPresetKeys.CUSTOM_11_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_11,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_11,
      placeholder: generateCustomPlaceholder('Custom 11'),
      valueKey: TargetingPresetKeys.CUSTOM_11,
      allowKey: TargetingPresetKeys.CUSTOM_11_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
  },
  Custom12: {
    key: TargetingPresetParamKeys.CUSTOM_12,
    label: 'Custom 12',
    categoryKey: TargetingPresetCategoryKeys.CUSTOM,
    apiKeys: [TargetingPresetKeys.CUSTOM_12, TargetingPresetKeys.CUSTOM_12_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_12,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_12,
      placeholder: generateCustomPlaceholder('Custom 12'),
      valueKey: TargetingPresetKeys.CUSTOM_12,
      allowKey: TargetingPresetKeys.CUSTOM_12_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
  },
  Custom13: {
    key: TargetingPresetParamKeys.CUSTOM_13,
    label: 'Custom 13',
    categoryKey: TargetingPresetCategoryKeys.CUSTOM,
    apiKeys: [TargetingPresetKeys.CUSTOM_13, TargetingPresetKeys.CUSTOM_13_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_13,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_13,
      placeholder: generateCustomPlaceholder('Custom 13'),
      valueKey: TargetingPresetKeys.CUSTOM_13,
      allowKey: TargetingPresetKeys.CUSTOM_13_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
  },
  Custom14: {
    key: TargetingPresetParamKeys.CUSTOM_14,
    label: 'Custom 14',
    categoryKey: TargetingPresetCategoryKeys.CUSTOM,
    apiKeys: [TargetingPresetKeys.CUSTOM_14, TargetingPresetKeys.CUSTOM_14_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_14,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_14,
      placeholder: generateCustomPlaceholder('Custom 14'),
      valueKey: TargetingPresetKeys.CUSTOM_14,
      allowKey: TargetingPresetKeys.CUSTOM_14_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
  },
  Custom15: {
    key: TargetingPresetParamKeys.CUSTOM_15,
    label: 'Custom 15',
    categoryKey: TargetingPresetCategoryKeys.CUSTOM,
    apiKeys: [TargetingPresetKeys.CUSTOM_15, TargetingPresetKeys.CUSTOM_15_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_15,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_15,
      placeholder: generateCustomPlaceholder('Custom 15'),
      valueKey: TargetingPresetKeys.CUSTOM_15,
      allowKey: TargetingPresetKeys.CUSTOM_15_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
  },
  Custom16: {
    key: TargetingPresetParamKeys.CUSTOM_16,
    label: 'Custom 16',
    categoryKey: TargetingPresetCategoryKeys.CUSTOM,
    apiKeys: [TargetingPresetKeys.CUSTOM_16, TargetingPresetKeys.CUSTOM_16_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_16,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_16,
      placeholder: generateCustomPlaceholder('Custom 16'),
      valueKey: TargetingPresetKeys.CUSTOM_16,
      allowKey: TargetingPresetKeys.CUSTOM_16_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
  },
  Custom17: {
    key: TargetingPresetParamKeys.CUSTOM_17,
    label: 'Custom 17',
    categoryKey: TargetingPresetCategoryKeys.CUSTOM,
    apiKeys: [TargetingPresetKeys.CUSTOM_17, TargetingPresetKeys.CUSTOM_17_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_17,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_17,
      placeholder: generateCustomPlaceholder('Custom 17'),
      valueKey: TargetingPresetKeys.CUSTOM_17,
      allowKey: TargetingPresetKeys.CUSTOM_17_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
  },
  Custom18: {
    key: TargetingPresetParamKeys.CUSTOM_18,
    label: 'Custom 18',
    categoryKey: TargetingPresetCategoryKeys.CUSTOM,
    apiKeys: [TargetingPresetKeys.CUSTOM_18, TargetingPresetKeys.CUSTOM_18_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_18,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_18,
      placeholder: generateCustomPlaceholder('Custom 18'),
      valueKey: TargetingPresetKeys.CUSTOM_18,
      allowKey: TargetingPresetKeys.CUSTOM_18_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
  },
  CustomTargeting: {
    key: TargetingPresetParamKeys.CUSTOM_TARGETING,
    label: 'Custom Targeting',
    categoryKey: TargetingPresetCategoryKeys.CUSTOM,
    apiKeys: [TargetingPresetKeys.CUSTOM_TARGETING, TargetingPresetKeys.CUSTOM_TARGETING_INCLUSION],
    customValidation: {
      validateKey: TargetingPresetKeys.CUSTOM_TARGETING,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CUSTOM_TARGETING,
      placeholder: generateCustomPlaceholder('Custom Targeting'),
      valueKey: TargetingPresetKeys.CUSTOM_TARGETING,
      allowKey: TargetingPresetKeys.CUSTOM_TARGETING_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
  },
  // DEVICE
  TargetedDevices: {
    key: TargetingPresetParamKeys.TARGETED_DEVICES,
    label: 'Targeted Devices',
    categoryKey: TargetingPresetCategoryKeys.DEVICE,
    apiKeys: [
      TargetingPresetKeys.TARGETING_DEVICES,
      TargetingPresetKeys.TARGETING_DEVICES_IS_ALLOWED,
    ],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'All Devices Selected',
      singularLabel: 'Device',
      listLabel: 'Devices',
      listKey: 'Devices',
      valueKey: TargetingPresetKeys.TARGETING_DEVICES,
      alowKey: TargetingPresetKeys.TARGETING_DEVICES_IS_ALLOWED,
      convertResponseValue: convertDevicesResponseValue,
      convertSelectedValue: convertDevicesSelectedValue,
      key: TargetingPresetKeys.TARGETING_DEVICES,
    } as ISelectWithIncludeAndLogicProps,
  },
  TargetedDeviceTypes: {
    key: TargetingPresetParamKeys.TARGETED_DEVICE_TYPES,
    label: 'Targeted Device Types',
    categoryKey: TargetingPresetCategoryKeys.DEVICE,
    apiKeys: [
      TargetingPresetKeys.TARGETING_DEVICE_TYPES,
      TargetingPresetKeys.TARGETING_DEVICE_TYPES_IS_ALLOWED,
    ],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'All Device Types Selected',
      singularLabel: 'Device Type',
      listLabel: 'Device Types',
      listKey: 'DeviceTypes',
      valueKey: TargetingPresetKeys.TARGETING_DEVICE_TYPES,
      allowKey: TargetingPresetKeys.TARGETING_DEVICE_TYPES_IS_ALLOWED,
      convertResponseValue: convertDeviceTypesResponseValue,
      convertSelectedValue: convertDeviceTypesSelectedValue,
      key: TargetingPresetKeys.TARGETING_DEVICE_TYPES,
      disableSort: true,
      isSelectionRequired: true,
    } as ISelectWithIncludeAndLogicProps,
  },
  TargetedDeviceSubtypes: {
    key: TargetingPresetParamKeys.TARGETED_DEVICE_SUBTYPES,
    label: 'Targeted Device Subtypes',
    categoryKey: TargetingPresetCategoryKeys.DEVICE,
    apiKeys: [
      TargetingPresetKeys.TARGETING_DEVICE_SUB_TYPES,
      TargetingPresetKeys.TARGETING_DEVICE_SUB_TYPES_IS_ALLOWED,
    ],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'All Device SubTypes Selected',
      singularLabel: 'Device SubType',
      listLabel: 'Device SubTypes',
      listKey: 'DeviceSubTypes',
      valueKey: TargetingPresetKeys.TARGETING_DEVICE_SUB_TYPES,
      allowKey: TargetingPresetKeys.TARGETING_DEVICE_SUB_TYPES_IS_ALLOWED,
      convertResponseValue: convertDeviceSubTypesResponseValue,
      convertSelectedValue: convertDeviceSubTypesSelectedValue,
      key: TargetingPresetKeys.TARGETING_DEVICE_TYPES,
      disableSort: true,
    } as ISelectWithIncludeAndLogicProps,
    checkIfHidden: (publisherID, isSuperUser) => !(isSuperUser || isPublisherSamsung(publisherID)),
  },
  IPFilters: {
    key: TargetingPresetParamKeys.IP_FILTERS,
    label: 'IP Filters *',
    categoryKey: TargetingPresetCategoryKeys.DEVICE,
    apiKeys: [TargetingPresetKeys.IPFILTER],
    isSuperUserOnly: true,
    customValidation: {
      validateKey: TargetingPresetKeys.IP_FILTER,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.IP_FILTERS,
      placeholder: generateGenericPlaceholder('IP Filters'),
      valueKey: TargetingPresetKeys.IPFILTER,
      tooltip: `Match this value to the IP set in your request to Publica's endpoint. Can be comma separated.`,
    } as ITextInputWithIncludeProps,
  },
  // APP
  AppBundleIDs: {
    key: TargetingPresetParamKeys.APP_BUNDLE_IDS,
    label: 'App Bundle IDs',
    categoryKey: TargetingPresetCategoryKeys.APP,
    apiKeys: [
      TargetingPresetKeys.TARGETING_APP_BUNDLE_ID,
      TargetingPresetKeys.TARGETING_APP_BUNDLE_ID_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_APP_BUNDLE_ID,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.APP_BUNDLE_IDS,
      placeholder: generateGenericPlaceholder('App Bundle IDs'),
      valueKey: TargetingPresetKeys.TARGETING_APP_BUNDLE_ID,
      allowKey: TargetingPresetKeys.TARGETING_APP_BUNDLE_ID_IS_ALLOWED,
      tooltip: AppBundleTooltipMessage,
    } as ITextInputWithIncludeProps,
  },
  // DEALS
  DealIDs: {
    key: TargetingPresetParamKeys.DEAL_IDS,
    label: 'Deal IDs',
    categoryKey: TargetingPresetCategoryKeys.DEALS,
    apiKeys: [TargetingPresetKeys.TARGETING_DEAL_ID],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_DEAL_ID,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.DEAL_IDS,
      placeholder: generateGenericPlaceholder('Deal IDs'),
      valueKey: TargetingPresetKeys.TARGETING_DEAL_ID,
    } as ITextInputWithIncludeProps,
  },
  FreewheelSectionIDs: {
    key: TargetingPresetParamKeys.FREEWHEEL_SECTION_IDS,
    label: 'Freewheel Section IDs',
    categoryKey: TargetingPresetCategoryKeys.DEALS,
    apiKeys: [
      TargetingPresetKeys.TARGETING_FW_SECTION_IDS,
      TargetingPresetKeys.TARGETING_FW_SECTION_IDS_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_FW_SECTION_IDS,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.FREEWHEEL_SECTION_IDS,
      placeholder: generateGenericPlaceholder('Section IDs'),
      valueKey: TargetingPresetKeys.TARGETING_FW_SECTION_IDS,
      allowKey: TargetingPresetKeys.TARGETING_FW_SECTION_IDS_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
  },
  // STREAMS
  TargetedLivestreams: {
    key: TargetingPresetParamKeys.TARGETED_LIVESTREAMS,
    label: 'Targeted Livestreams',
    categoryKey: TargetingPresetCategoryKeys.STREAMS,
    apiKeys: [TargetingPresetKeys.TARGETING_LIVESTREAM],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'All Livestreams Selected',
      singularLabel: 'Livestream',
      listLabel: 'Livestreams',
      listKey: 'Livestreams',
      valueKey: TargetingPresetKeys.TARGETING_LIVESTREAM,
      convertResponseValue: convertLivestreamResponseValue,
      convertSelectedValue: convertLivestreamSelectedValue,
      key: TargetingPresetKeys.TARGETING_LIVESTREAM,
      disableSort: true,
    } as ISelectWithIncludeAndLogicProps,
  },
  // SEGMENTS
  AudienceSegments: {
    key: TargetingPresetParamKeys.AUDIENCE_SEGMENTS,
    label: 'Audience Segments',
    categoryKey: TargetingPresetCategoryKeys.SEGMENTS,
    apiKeys: [
      TargetingPresetKeys.TARGETING_AUDIENCE_SEGMENT_IDS,
      TargetingPresetKeys.TARGETING_AUDIENCE_SEGMENT_IDS_IS_ALLOWED,
      TargetingPresetKeys.SEGMENT_TARGETING,
    ],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'Select Audience Segments',
      singularLabel: 'Audience Segment',
      listLabel: 'Audience Segments',
      listKey: 'AudienceSegments',
      valueKey: TargetingPresetKeys.TARGETING_AUDIENCE_SEGMENT_IDS,
      allowKey: TargetingPresetKeys.TARGETING_AUDIENCE_SEGMENT_IDS_IS_ALLOWED,
      logicKey: TargetingPresetKeys.SEGMENT_TARGETING,
      enableActiveFilter: true,
      enableLabelTooltip: true,
      displayID: true,
      key: TargetingPresetKeys.TARGETING_AUDIENCE_SEGMENT_IDS,
      tooltip: 'Choose which audience segment to bid on.',
    } as ISelectWithIncludeAndLogicProps,
  },
  ContextualSegments: {
    key: TargetingPresetParamKeys.CONTEXTUAL_SEGMENTS,
    label: 'Contextual Segments',
    categoryKey: TargetingPresetCategoryKeys.SEGMENTS,
    apiKeys: [TargetingPresetKeys.CONTEXTUAL_SEGMENT_IDS],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'Select Contextual Segments',
      singularLabel: 'Contextual Segment',
      listLabel: 'Contextual Segments',
      listKey: 'ContextualSegments',
      valueKey: TargetingPresetKeys.CONTEXTUAL_SEGMENT_IDS,
      enableActiveFilter: true,
      enableLabelTooltip: true,
      displayID: true,
      key: TargetingPresetKeys.CONTEXTUAL_SEGMENT_IDS,
      tooltip: 'Choose which contextual segment to bid on.',
    } as ISelectWithIncludeAndLogicProps,
  },
  // SITE PAGES
  SitePages: {
    key: TargetingPresetParamKeys.SITE_PAGES,
    label: 'Site Pages',
    categoryKey: TargetingPresetCategoryKeys.SITE_PAGES,
    apiKeys: [
      TargetingPresetKeys.TARGETING_SITE_PAGES,
      TargetingPresetKeys.TARGETING_SITE_PAGES_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_SITE_PAGES,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.SITE_PAGES,
      placeholder: generateGenericPlaceholder('Site Pages'),
      valueKey: TargetingPresetKeys.TARGETING_SITE_PAGES,
      allowKey: TargetingPresetKeys.TARGETING_SITE_PAGES_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
  },
  // SITE DOMAINS
  SiteDomains: {
    key: TargetingPresetParamKeys.SITE_DOMAINS,
    label: 'Site Domains',
    categoryKey: TargetingPresetCategoryKeys.SITE_DOMAINS,
    apiKeys: [
      TargetingPresetKeys.TARGETING_SITE_DOMAIN,
      TargetingPresetKeys.TARGETING_SITE_DOMAIN_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: TargetingPresetKeys.TARGETING_SITE_DOMAIN,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.SITE_DOMAINS,
      placeholder: generateGenericPlaceholder('Site Domains'),
      valueKey: TargetingPresetKeys.TARGETING_SITE_DOMAIN,
      allowKey: TargetingPresetKeys.TARGETING_SITE_DOMAIN_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
  },
};

export const optionKeys: TargetingPresetParamKey[] = [
  // INVENTORY
  // TargetingPresetParamKeys.CHANNELS,
  // TargetingPresetParamKeys.CHANNEL_GROUPS,
  // TargetingPresetParamKeys.CHANNEL_LISTS,
  TargetingPresetParamKeys.SLOTS,
  TargetingPresetParamKeys.ENFORCE_SLOT_TARGETING,
  TargetingPresetParamKeys.POSITION,
  TargetingPresetParamKeys.POD_SEQUENCE,
  TargetingPresetParamKeys.REQUESTED_POD_DURATION,
  TargetingPresetParamKeys.COMBINE_VMAP_REQUESTS, // DB: AdbreakTargeting
  TargetingPresetParamKeys.AD_BREAK, // DB: TargetedAdBreak
  TargetingPresetParamKeys.GIVN_TARGETING,
  // CONTENT
  TargetingPresetParamKeys.CONTENT_CHANNEL,
  TargetingPresetParamKeys.CONTENT_DISTRIBUTOR,
  TargetingPresetParamKeys.CONTENT_GENRE,
  TargetingPresetParamKeys.CONTENT_ID,
  TargetingPresetParamKeys.CONTENT_NETWORK,
  TargetingPresetParamKeys.CONTENT_PRODUCER,
  TargetingPresetParamKeys.CONTENT_RATING,
  TargetingPresetParamKeys.CONTENT_SERIES,
  TargetingPresetParamKeys.CONTENT_TITLE,
  TargetingPresetParamKeys.CONTENT_CATEGORY,
  // GEO
  TargetingPresetParamKeys.TARGETED_REGIONS, // COUNTRY GROUPS
  TargetingPresetParamKeys.TARGETED_COUNTRIES,
  TargetingPresetParamKeys.LOCAL_TARGETING,
  // DAYPARTING
  TargetingPresetParamKeys.DAYPARTING,
  // PRIVACY
  TargetingPresetParamKeys.COPPA,
  TargetingPresetParamKeys.LIMITED_AD_TRACKING,
  TargetingPresetParamKeys.CCPA,
  // CUSTOM
  TargetingPresetParamKeys.CUSTOM_4,
  TargetingPresetParamKeys.CUSTOM_5,
  TargetingPresetParamKeys.CUSTOM_6,
  TargetingPresetParamKeys.CUSTOM_7,
  TargetingPresetParamKeys.CUSTOM_8,
  TargetingPresetParamKeys.CUSTOM_9,
  TargetingPresetParamKeys.CUSTOM_10,
  TargetingPresetParamKeys.CUSTOM_11,
  TargetingPresetParamKeys.CUSTOM_12,
  TargetingPresetParamKeys.CUSTOM_13,
  TargetingPresetParamKeys.CUSTOM_14,
  TargetingPresetParamKeys.CUSTOM_15,
  TargetingPresetParamKeys.CUSTOM_16,
  TargetingPresetParamKeys.CUSTOM_17,
  TargetingPresetParamKeys.CUSTOM_18,
  TargetingPresetParamKeys.CUSTOM_TARGETING,
  // DEVICE
  TargetingPresetParamKeys.TARGETED_DEVICES,
  TargetingPresetParamKeys.TARGETED_DEVICE_TYPES,
  TargetingPresetParamKeys.TARGETED_DEVICE_SUBTYPES,
  TargetingPresetParamKeys.IP_FILTERS,
  // APP
  TargetingPresetParamKeys.APP_BUNDLE_IDS,
  // DEALS
  TargetingPresetParamKeys.DEAL_IDS,
  TargetingPresetParamKeys.FREEWHEEL_SECTION_IDS,
  // STREAMS
  TargetingPresetParamKeys.TARGETED_LIVESTREAMS,
  // SEGMENTS
  TargetingPresetParamKeys.AUDIENCE_SEGMENTS,
  TargetingPresetParamKeys.CONTEXTUAL_SEGMENTS,
  // SITE PAGES
  TargetingPresetParamKeys.SITE_PAGES,
  // SITE DOMAINS
  TargetingPresetParamKeys.SITE_DOMAINS,
];

export interface IParameterDropdown {
  key: string;
  getList: (session: ISessionState, user: IUserState) => Promise<ISelectOption[]>;
}

export interface ITargetingPresetHeaderBidderFormConfig {
  params: Record<TargetingPresetParamKey, ITargetingPresetParam>;
  dropdowns: Record<string, IParameterDropdown>;
}

export const targetingPresetHeaderBidderFormConfig: ITargetingPresetHeaderBidderFormConfig = {
  params: TargetingPresetParameters,
  dropdowns: {
    // TODO: SAVE FOR NOW BUT REMOVE BEFORE GENERAL RELEASE
    // Channels: {
    //   key: 'Channels',
    //   getList: generateChannelsOptionsList,
    // },
    // ChannelGroups: {
    //   key: 'ChannelGroups',
    //   getList: generateChannelGroupsOptionsList,
    // },
    // ChannelList: {
    //   key: 'ChannelLists',
    //   getList: generateChannelListsOptionsList,
    // },
    Slots: {
      key: 'Slots',
      getList: () => generateOptionsListAsPromise(SlotOptions),
    },
    TargetingPositions: {
      key: 'TargetingPositions',
      getList: () => generateOptionsListAsPromise(PositionTargetingOptions),
    },
    PodSequences: {
      key: 'PodSequences',
      getList: () => generateOptionsListAsPromise(PodSequenceTargetingOptions),
    },
    AdBreaks: {
      key: 'AdBreaks',
      getList: () => generateOptionsListAsPromise(TargetedAdBreakOptions),
    },
    Countries: {
      key: 'Countries',
      getList: () => generateOptionsListAsPromise(generateCountryOptions()),
    },
    CountryGroups: {
      key: 'CountryGroups',
      getList: generateCountryGroupOptionsList,
    },
    DMAs: {
      key: 'DMAs',
      getList: () => generateOptionsListAsPromise(generateDMAOptions('us')),
    },
    COPPA: {
      key: 'COPPA',
      getList: () => generateOptionsListAsPromise(TargetingCOPPAOptions),
    },
    LimitedAdTracking: {
      key: 'LimitedAdTracking',
      getList: () => generateOptionsListAsPromise(TargetBoolOptions),
    },
    CCPA: {
      key: 'CCPA',
      getList: () => generateOptionsListAsPromise(TargetingCCPAOptions),
    },
    Devices: {
      key: 'Devices',
      getList: () => generateOptionsListAsPromise(DeviceOptions),
    },
    DeviceTypes: {
      key: 'DeviceTypes',
      getList: () => generateOptionsListAsPromise(DeviceORTBTypeOptions),
    },
    DeviceSubTypes: {
      key: 'DeviceSubTypes',
      getList: generateDeviceSubTypeOptionsList,
    },
    Livestreams: {
      key: 'Livestreams',
      getList: () => generateOptionsListAsPromise(TargetingLiveStreamOptions),
    },
    AudienceSegments: {
      key: 'AudienceSegments',
      getList: generateAudienceSegmentOptionsList,
    },
    ContextualSegments: {
      key: 'ContextualSegments',
      getList: generateContextualSegmentOptionsList,
    },
  },
};
